/**
 * Vendor Management Dashboard
 * 
 * Admin interface for managing contractor/vendor network
 * 
 * Features:
 * - Vendor list with search/filter/sort
 * - Document verification workflow
 * - Service area management
 * - Performance scorecards
 * - Rate band adjustments
 * - Payment history
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Vendor {
  id: string;
  company_name: string;
  dba_name?: string;
  primary_contact_name: string;
  primary_contact_phone: string;
  primary_contact_email: string;
  contractor_license_number: string;
  license_expiry_date: string;
  status: 'pending' | 'active' | 'suspended' | 'terminated';
  rate_band: 'A' | 'B' | 'C' | 'D';
  services_offered: string[];
  created_at: string;
}

interface VendorDocument {
  id: string;
  document_type: string;
  file_name: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  expiry_date?: string;
  uploaded_at: string;
}

export function VendorManagement() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterRateBand, setFilterRateBand] = useState<string>('all');
  const [view, setView] = useState<'list' | 'detail' | 'documents'>('list');

  useEffect(() => {
    loadVendors();
  }, [filterStatus, filterRateBand]);

  useEffect(() => {
    if (selectedVendor) {
      loadVendorDocuments(selectedVendor.id);
    }
  }, [selectedVendor]);

  const loadVendors = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('vendors')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }
      
      if (filterRateBand !== 'all') {
        query = query.eq('rate_band', filterRateBand);
      }

      const { data, error } = await query;

      if (error) throw error;
      setVendors(data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
      alert('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const loadVendorDocuments = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('vendor_documents')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleApproveVendor = async (vendorId: string) => {
    if (!confirm('Approve this vendor for active status?')) return;

    try {
      const { error } = await supabase
        .from('vendors')
        .update({
          status: 'active',
          activated_at: new Date().toISOString()
        })
        .eq('id', vendorId);

      if (error) throw error;
      
      alert('Vendor approved successfully');
      await loadVendors();
      
      if (selectedVendor?.id === vendorId) {
        setSelectedVendor({ ...selectedVendor, status: 'active' });
      }
    } catch (error) {
      console.error('Error approving vendor:', error);
      alert('Failed to approve vendor');
    }
  };

  const handleSuspendVendor = async (vendorId: string) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      const { error } = await supabase
        .from('vendors')
        .update({
          status: 'suspended',
          suspension_reason: reason,
          suspension_date: new Date().toISOString()
        })
        .eq('id', vendorId);

      if (error) throw error;
      
      alert('Vendor suspended');
      await loadVendors();
    } catch (error) {
      console.error('Error suspending vendor:', error);
      alert('Failed to suspend vendor');
    }
  };

  const handleUpdateRateBand = async (vendorId: string, newBand: 'A' | 'B' | 'C' | 'D') => {
    if (!confirm(`Change rate band to ${newBand}?`)) return;

    try {
      const { error } = await supabase
        .from('vendors')
        .update({ rate_band: newBand })
        .eq('id', vendorId);

      if (error) throw error;
      
      alert(`Rate band updated to ${newBand}`);
      await loadVendors();
      
      if (selectedVendor?.id === vendorId) {
        setSelectedVendor({ ...selectedVendor, rate_band: newBand });
      }
    } catch (error) {
      console.error('Error updating rate band:', error);
      alert('Failed to update rate band');
    }
  };

  const handleApproveDocument = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('vendor_documents')
        .update({
          status: 'approved',
          verified_at: new Date().toISOString()
        })
        .eq('id', documentId);

      if (error) throw error;
      
      alert('Document approved');
      if (selectedVendor) {
        await loadVendorDocuments(selectedVendor.id);
      }
    } catch (error) {
      console.error('Error approving document:', error);
      alert('Failed to approve document');
    }
  };

  const handleRejectDocument = async (documentId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      const { error } = await supabase
        .from('vendor_documents')
        .update({
          status: 'rejected',
          rejection_reason: reason
        })
        .eq('id', documentId);

      if (error) throw error;
      
      alert('Document rejected');
      if (selectedVendor) {
        await loadVendorDocuments(selectedVendor.id);
      }
    } catch (error) {
      console.error('Error rejecting document:', error);
      alert('Failed to reject document');
    }
  };

  const filteredVendors = vendors.filter(vendor =>
    searchTerm === '' ||
    vendor.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contractor_license_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.primary_contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'terminated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRateBandColor = (band: string) => {
    switch (band) {
      case 'A': return 'bg-purple-100 text-purple-800';
      case 'B': return 'bg-blue-100 text-blue-800';
      case 'C': return 'bg-green-100 text-green-800';
      case 'D': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Vendor Management</h1>
            <p className="text-sm text-gray-600">
              Manage contractor network, documents, and performance
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadVendors()}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
            >
              🔄 Refresh
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Add Vendor
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mt-4 flex gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, license, email..."
            className="flex-1 px-4 py-2 border rounded-lg"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
          <select
            value={filterRateBand}
            onChange={(e) => setFilterRateBand(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Rates</option>
            <option value="A">Rate Band A</option>
            <option value="B">Rate Band B</option>
            <option value="C">Rate Band C</option>
            <option value="D">Rate Band D</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Vendor List */}
        <div className="w-1/3 bg-white border-r overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredVendors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No vendors found
            </div>
          ) : (
            <div className="divide-y">
              {filteredVendors.map(vendor => (
                <div
                  key={vendor.id}
                  onClick={() => {
                    setSelectedVendor(vendor);
                    setView('detail');
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedVendor?.id === vendor.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{vendor.company_name}</div>
                      {vendor.dba_name && (
                        <div className="text-sm text-gray-600">DBA: {vendor.dba_name}</div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vendor.status)}`}>
                      {vendor.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>License: {vendor.contractor_license_number}</div>
                    <div>{vendor.primary_contact_email}</div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${getRateBandColor(vendor.rate_band)}`}>
                      Rate Band {vendor.rate_band}
                    </span>
                    <span className="px-2 py-1 rounded text-xs bg-gray-100">
                      {vendor.services_offered.length} services
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="flex-1 overflow-y-auto">
          {!selectedVendor ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              Select a vendor to view details
            </div>
          ) : view === 'detail' ? (
            <div className="p-6">
              {/* Vendor Header */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedVendor.company_name}</h2>
                    {selectedVendor.dba_name && (
                      <p className="text-gray-600">DBA: {selectedVendor.dba_name}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded ${getStatusColor(selectedVendor.status)}`}>
                      {selectedVendor.status}
                    </span>
                    <span className={`px-3 py-1 rounded ${getRateBandColor(selectedVendor.rate_band)}`}>
                      Rate Band {selectedVendor.rate_band}
                    </span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  {selectedVendor.status === 'pending' && (
                    <button
                      onClick={() => handleApproveVendor(selectedVendor.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      ✓ Approve
                    </button>
                  )}
                  {selectedVendor.status === 'active' && (
                    <button
                      onClick={() => handleSuspendVendor(selectedVendor.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    onClick={() => setView('documents')}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    📄 View Documents
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    📊 Performance
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    💰 Payments
                  </button>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="font-bold mb-4">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Primary Contact</div>
                    <div className="font-medium">{selectedVendor.primary_contact_name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Phone</div>
                    <div className="font-medium">{selectedVendor.primary_contact_phone}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Email</div>
                    <div className="font-medium">{selectedVendor.primary_contact_email}</div>
                  </div>
                </div>
              </div>

              {/* License Information */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="font-bold mb-4">License & Compliance</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">License Number</div>
                    <div className="font-medium">{selectedVendor.contractor_license_number}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">License Expiry</div>
                    <div className="font-medium">
                      {new Date(selectedVendor.license_expiry_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Services & Capabilities */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="font-bold mb-4">Services & Capabilities</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedVendor.services_offered.map(service => (
                    <span key={service} className="px-3 py-1 bg-blue-100 text-blue-800 rounded">
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              {/* Rate Band Management */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-bold mb-4">Rate Band Management</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Current: <strong>Band {selectedVendor.rate_band}</strong>
                </p>
                <div className="flex gap-2">
                  {(['A', 'B', 'C', 'D'] as const).map(band => (
                    <button
                      key={band}
                      onClick={() => handleUpdateRateBand(selectedVendor.id, band)}
                      disabled={selectedVendor.rate_band === band}
                      className={`px-4 py-2 rounded ${
                        selectedVendor.rate_band === band
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      Band {band}
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <div><strong>A:</strong> Premium (highest rates, best performance)</div>
                  <div><strong>B:</strong> Standard (standard rates, good performance)</div>
                  <div><strong>C:</strong> Economy (lower rates, acceptable performance)</div>
                  <div><strong>D:</strong> Probationary (lowest rates, new/poor performance)</div>
                </div>
              </div>
            </div>
          ) : view === 'documents' ? (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Documents</h2>
                <button
                  onClick={() => setView('detail')}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  ← Back to Details
                </button>
              </div>

              {documents.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  No documents uploaded yet
                </div>
              ) : (
                <div className="space-y-4">
                  {documents.map(doc => (
                    <div key={doc.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold">{doc.document_type.toUpperCase()}</span>
                            <span className={`px-2 py-1 rounded text-xs ${getDocumentStatusColor(doc.status)}`}>
                              {doc.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <div>{doc.file_name}</div>
                            <div>Uploaded: {new Date(doc.uploaded_at).toLocaleString()}</div>
                            {doc.expiry_date && (
                              <div>Expires: {new Date(doc.expiry_date).toLocaleDateString()}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                          >
                            View
                          </button>
                          {doc.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApproveDocument(doc.id)}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => handleRejectDocument(doc.id)}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default VendorManagement;
